import { useState, useEffect, useRef } from "react";
import { AxiosError } from "axios";
import {
  Modal,
  Box,
  Button,
  TextField,
  useTheme,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  modalBoxStyles,
  mainButtonStyles,
  secondaryButtonStyles,
  fieldContainerStyles,
  getTextFieldStyles,
  popperStyles,
  layoutStyles,
  dayStyles,
  yearButtonStyles,
  buttonsContainerStyles,
  closeButtonContainerStyles,
  smallModalBoxStyles,
  menuItemStyles,
  getInputLabelStyles,
  getSelectStyles,
  errorTypographyStyles,
  formBlockStyles,
  topContainerStyles,
} from "./styles";
import {
  createUser,
  getAdminCompanyStructure,
  getAdminDomainTree,
} from "../../../axios/admin";
import { DomainTree } from "../../../utils/domainFocus";
import { CustomTypography } from "../../custom-typography/custom-typography";
import { AdminCreds, UserInfo } from "../../../types";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { MdOutlineFileUpload } from "react-icons/md";
import dayjs from "dayjs";
import { CsvUploadModal } from "./csv-upload-modal";

interface CreateMessageFormProps {
  open: boolean;
  onClose: () => void;
  adminCreds: AdminCreds;
}

interface Errors {
  form?: string | null;
  fullName?: string | null;
  login?: string | null;
  email?: string | null;
  password?: string | null;
  expirationDate?: string | null;
  allowedFocusArea?: string | null;
  allowedDomains?: string | null;
  powerBiEmail?: string | null;
  job?: string | null;
  company?: string | null;
  department?: string | null;
  companyStructure?: string | null;
  dateOfBirth?: string | null;
  isDemo?: string | null;
  notDisplayInStatistics?: string | null;
}

const getErrorMessage = (error: unknown, defaultMessage?: string): string => {
  if (error && typeof error === "object") {
    if (error instanceof AxiosError) {
      if (error.response?.data.detail) return error.response?.data.detail;
    } else if ("message" in error && typeof error.message === "string") {
      return error.message;
    }
  } else if (typeof error === "string") {
    return error;
  }

  if (defaultMessage) return defaultMessage;

  return "Oops... Unknown error happened while creating a user";
};

const validateEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return regex.test(email);
};

export const CreateUserForm = ({
  open,
  onClose,
  adminCreds,
}: CreateMessageFormProps) => {
  const plusOneMonth = new Date();
  plusOneMonth.setMonth(plusOneMonth.getMonth() + 1);

  const [domainTree, setDomainTree] = useState<DomainTree | null>(null);
  const [companyStructure, setCompanyStructure] = useState<{
    [key: string]: {
      [key: string]: string[];
    };
  } | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    language: "English",
    token_limit: 0,
    allowed_domains: [],
    allowed_Focus_Area: [],
    professional_information: {
      role: "user",
      country: [],
      city: [],
      line: [],
    },
    personal_information: {},
    config: {},
  });
  const [isCsvUploadOpen, setIsCsvUploadOpen] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [tokenLimitStr, setTokenLimitStr] = useState("0");
  const [expirationDateTimestamp, setExpirationDateTimestamp] =
    useState<number>(plusOneMonth.getTime());
  const [birthDateTimestamp, setBirthDateTimestamp] = useState<number | null>(
    null
  );
  const [isExpirationDatePickerOpen, setIsExpirationDatePickerOpen] =
    useState(false);
  const [isBirthDatePickerOpen, setIsBirthDatePickerOpen] = useState(false);
  const [sendingResult, setSendingResult] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const modalRef = useRef<HTMLDivElement | null>(null);

  const theme = useTheme();

  useEffect(() => {
    const fetchDomainTree = async () => {
      try {
        const fetchedDomainTree = await getAdminDomainTree(adminCreds);
        setDomainTree(fetchedDomainTree);
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        console.error("Error fetching domain tree:", errorMessage);
        setErrors((prev) => ({
          ...prev,
          allowedDomains: "Cannot get available domains",
        }));
      }
    };

    const fetchCompanyStructure = async () => {
      try {
        const fetchedCompanyStructure =
          await getAdminCompanyStructure(adminCreds);
        setCompanyStructure(fetchedCompanyStructure);
      } catch (error) {
        const errorMessage = getErrorMessage(error);
        console.error("Error fetching company structure:", errorMessage);
        setErrors((prev) => ({
          ...prev,
          companyStructure: "Cannot get company structure",
        }));
      }
    };

    Promise.all([fetchDomainTree(), fetchCompanyStructure()]);
  }, [adminCreds]);

  useEffect(() => {
    const sendUserInfo = async () => {
      if (userInfo.send) {
        setLoading(true);
        try {
          const res = await createUser(userInfo, adminCreds);
          setSendingResult(res);
          setUserInfo({
            language: "English",
            token_limit: 0,
            allowed_domains: [],
            allowed_Focus_Area: [],
            professional_information: {
              role: "user",
              country: [],
              city: [],
              line: [],
            },
            personal_information: {},
            config: {},
          });
          setTokenLimitStr("0");
          setExpirationDateTimestamp(plusOneMonth.getTime());
          setBirthDateTimestamp(null);
        } catch (error) {
          const errorMessage = getErrorMessage(error);
          console.error("Error while creating a user:", errorMessage);
          setErrors((prev) => ({ ...prev, form: errorMessage }));
          modalRef.current?.scrollTo(0, 0);
        } finally {
          setLoading(false);
          setUserInfo((prev) => ({ ...prev, send: false }));
        }
      }
    };

    sendUserInfo();
  }, [userInfo, adminCreds]);

  const handleOpenCsvUpload = () => {
    setIsCsvUploadOpen(true);
  };

  const handleCloseCsvUpload = () => {
    setIsCsvUploadOpen(false);
  };

  const handleSuccessfulCsvUpload = (res: string) => {
    setSendingResult(res);
    setUserInfo({
      language: "English",
      token_limit: 0,
      allowed_domains: [],
      allowed_Focus_Area: [],
      professional_information: {
        role: "user",
        country: [],
        city: [],
        line: [],
      },
      personal_information: {},
      config: {},
    });
    setTokenLimitStr("0");
    setExpirationDateTimestamp(Date.now());
    setBirthDateTimestamp(null);
  };

  const handleClose = () => {
    setSendingResult(null);
    onClose();
  };

  const handleSubmit = async () => {
    setErrors((prev) => ({ ...prev, form: null }));

    let isError = false;

    if (!userInfo.full_name) {
      setErrors((prev) => ({ ...prev, fullName: "Enter user's full name" }));
      isError = true;
    }

    if (!userInfo.login) {
      setErrors((prev) => ({ ...prev, login: "Enter user's login" }));
      isError = true;
    }

    if (!userInfo.email || !validateEmail(userInfo.email)) {
      setErrors((prev) => ({ ...prev, email: "Enter a valid user's email" }));
      isError = true;
    }

    if (!userInfo.password) {
      setErrors((prev) => ({
        ...prev,
        password: "Enter user's password",
      }));
      isError = true;
    }

    const expirationDate = new Date(expirationDateTimestamp);
    const validationDate = plusOneMonth;
    validationDate.setDate(validationDate.getDate() - 1);

    if (validationDate > expirationDate) {
      setErrors((prev) => ({
        ...prev,
        expirationDate: "Expiration date must be at least one month later",
      }));
      isError = true;
    } else {
      const yearStr = `${expirationDate.getFullYear()}`;
      const monthStr = `${expirationDate.getMonth() + 1}`.padStart(2, "0");
      const dayStr = `${expirationDate.getDate()}`.padStart(2, "0");
      const expirationDateStr = `${yearStr}-${monthStr}-${dayStr}`;
      setUserInfo((prev) => ({ ...prev, expiration_date: expirationDateStr }));
    }

    if (userInfo.allowed_domains.length === 0) {
      setErrors((prev) => ({
        ...prev,
        allowedDomains: "Select allowed domains",
      }));
      isError = true;
    }

    if (userInfo.allowed_Focus_Area.length === 0) {
      setErrors((prev) => ({
        ...prev,
        allowedFocusArea: "Select allowed focus area",
      }));
      isError = true;
    }

    if (!userInfo.power_bi_email || !validateEmail(userInfo.power_bi_email)) {
      setErrors((prev) => ({
        ...prev,
        powerBiEmail: "Enter a valid user's Power Bi Email",
      }));
      isError = true;
    }

    if (userInfo.is_demo === undefined) {
      setErrors((prev) => ({
        ...prev,
        isDemo: "Select whether the user is demo",
      }));
      isError = true;
    }

    if (userInfo.not_display_in_statistics === undefined) {
      setErrors((prev) => ({
        ...prev,
        notDisplayInStatistics:
          "Select whether to hide the user from statistics",
      }));
      isError = true;
    }

    if (!userInfo.professional_information.job) {
      setErrors((prev) => ({
        ...prev,
        job: "Enter user's job",
      }));
      isError = true;
    }

    if (!userInfo.professional_information.company) {
      setErrors((prev) => ({
        ...prev,
        company: "Enter user's company",
      }));
      isError = true;
    }

    if (!userInfo.professional_information.department) {
      setErrors((prev) => ({
        ...prev,
        department: "Enter user's department",
      }));
      isError = true;
    }

    if (!birthDateTimestamp) {
      setErrors((prev) => ({
        ...prev,
        dateOfBirth: "Enter user's date of birth",
      }));
      isError = true;
    } else {
      const eighteenYearsLater = new Date(birthDateTimestamp);
      eighteenYearsLater.setFullYear(eighteenYearsLater.getFullYear() + 18);

      if (new Date() < eighteenYearsLater) {
        setErrors((prev) => ({
          ...prev,
          dateOfBirth: "User must be at least 18 years old",
        }));
        isError = true;
      }

      const birthDate = new Date(birthDateTimestamp);
      const yearStr = `${birthDate.getFullYear()}`;
      const monthStr = `${birthDate.getMonth() + 1}`.padStart(2, "0");
      const dayStr = `${birthDate.getDate()}`.padStart(2, "0");
      const birthDateStr = `${yearStr}-${monthStr}-${dayStr}`;
      setUserInfo((prev) => ({
        ...prev,
        personal_information: {
          ...prev.personal_information,
          date_of_birth: birthDateStr,
        },
      }));
    }

    setUserInfo((prev) => ({ ...prev, token_limit: +tokenLimitStr }));

    for (const error of Object.values(errors)) {
      if (error) {
        isError = true;
        break;
      }
    }

    if (isError) {
      modalRef.current?.scrollTo(0, 0);
      return;
    }

    setUserInfo((prev) => ({ ...prev, send: true }));
  };

  if (sendingResult) {
    return (
      <Modal open={open} onClose={handleClose}>
        <Box sx={smallModalBoxStyles}>
          <CustomTypography
            fontSize={20}
            fontWeight={600}
            sx={{
              color: theme.palette.modalColors.headerColor,
            }}
          >
            {sendingResult}
          </CustomTypography>

          <Box sx={closeButtonContainerStyles}>
            <Button
              variant="contained"
              onClick={handleClose}
              sx={mainButtonStyles}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    );
  }

  return (
    <>
      <Modal open={open && !isCsvUploadOpen} onClose={handleClose}>
        <Box sx={modalBoxStyles} ref={modalRef}>
          {loading && (
            <CustomTypography
              variant="body2"
              sx={{
                textAlign: "center",
                fontSize: "1.1em",
                color: theme.palette.adminPanel.textColor,
              }}
            >
              Loading...
            </CustomTypography>
          )}
          {!loading && (
            <form className="flex flex-col gap-[16px]">
              <Box sx={topContainerStyles}>
                <Box sx={fieldContainerStyles}>
                  <CustomTypography
                    fontSize={20}
                    fontWeight={600}
                    sx={{
                      color: theme.palette.modalColors.headerColor,
                    }}
                  >
                    Enter user info
                  </CustomTypography>

                  {errors.form && (
                    <Typography variant="body2" sx={errorTypographyStyles}>
                      {errors.form}
                    </Typography>
                  )}
                </Box>

                <Button
                  variant="contained"
                  onClick={handleOpenCsvUpload}
                  sx={mainButtonStyles}
                >
                  <MdOutlineFileUpload
                    style={{
                      transform: "scale(2, 2) translate(-5px, 0)",
                    }}
                  />{" "}
                  <span>Upload a CSV File</span>
                </Button>
              </Box>

              <Box sx={fieldContainerStyles}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="full-name"
                  label="Full name"
                  value={userInfo.full_name ?? ""}
                  onChange={(e) =>
                    setUserInfo((prev) => ({
                      ...prev,
                      full_name: e.target.value,
                    }))
                  }
                  onFocus={() =>
                    setErrors((prev) => ({
                      ...prev,
                      form: null,
                      fullName: null,
                    }))
                  }
                  sx={getTextFieldStyles(Boolean(errors.fullName))}
                />

                {errors.fullName && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.fullName}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="login"
                  label="Login"
                  value={userInfo.login ?? ""}
                  onChange={(e) =>
                    setUserInfo((prev) => ({ ...prev, login: e.target.value }))
                  }
                  onFocus={() =>
                    setErrors((prev) => ({ ...prev, form: null, login: null }))
                  }
                  sx={getTextFieldStyles(Boolean(errors.login))}
                />

                {errors.login && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.login}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="email"
                  label="Email"
                  type="email"
                  value={userInfo.email ?? ""}
                  onChange={(e) =>
                    setUserInfo((prev) => ({ ...prev, email: e.target.value }))
                  }
                  onFocus={() =>
                    setErrors((prev) => ({ ...prev, form: null, email: null }))
                  }
                  sx={getTextFieldStyles(Boolean(errors.email))}
                />

                {errors.email && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.email}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="password"
                  label="Password"
                  value={userInfo.password ?? ""}
                  onChange={(e) =>
                    setUserInfo((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  onFocus={() =>
                    setErrors((prev) => ({
                      ...prev,
                      form: null,
                      password: null,
                    }))
                  }
                  sx={getTextFieldStyles(Boolean(errors.password))}
                />

                {errors.password && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.password}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <FormControl>
                  <InputLabel
                    id="language-select-label"
                    htmlFor="language"
                    sx={getInputLabelStyles(false)}
                  >
                    Language
                  </InputLabel>
                  <Select
                    labelId="language-select-label"
                    label="Language"
                    value={userInfo.language}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    onFocus={() =>
                      setErrors((prev) => ({ ...prev, form: null }))
                    }
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        language: e.target.value,
                      }))
                    }
                    slotProps={{
                      input: {
                        id: "language",
                      },
                    }}
                    sx={getSelectStyles(false)}
                  >
                    <MenuItem key="English" value="English" sx={menuItemStyles}>
                      English
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={fieldContainerStyles}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="token-limit"
                  label="Token limit"
                  value={tokenLimitStr}
                  inputProps={{ min: 0, step: 1 }}
                  onChange={(e) => {
                    let value = e.target.value.replace(/\D/g, "");
                    value = value.replace(/^0+/, "");
                    if (value === "") {
                      value = "0";
                    }
                    setTokenLimitStr(value);
                  }}
                  onFocus={() => setErrors((prev) => ({ ...prev, form: null }))}
                  sx={getTextFieldStyles(false)}
                />
              </Box>

              <Box sx={fieldContainerStyles}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Choose expiration date"
                    value={dayjs(new Date(expirationDateTimestamp))}
                    onChange={(newValue) => {
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        expirationDate: null,
                      }));

                      if (!newValue) {
                        return;
                      }

                      setExpirationDateTimestamp(newValue.valueOf());
                    }}
                    minDate={dayjs(plusOneMonth)}
                    format="DD.MM.YYYY"
                    disablePast
                    open={isExpirationDatePickerOpen}
                    onOpen={() => {
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        expirationDate: null,
                      }));
                      setIsExpirationDatePickerOpen(true);
                    }}
                    onClose={() => setIsExpirationDatePickerOpen(false)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        onClick: () => setIsExpirationDatePickerOpen(true),
                        sx: getTextFieldStyles(Boolean(errors.expirationDate)),
                      },
                      popper: {
                        sx: popperStyles,
                      },
                      layout: {
                        sx: layoutStyles,
                      },
                      day: {
                        sx: dayStyles,
                      },
                      yearButton: {
                        sx: yearButtonStyles,
                      },
                    }}
                  />
                </LocalizationProvider>

                {errors.expirationDate && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.expirationDate}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <FormControl>
                  <InputLabel
                    id="domain-select-label"
                    htmlFor="domain"
                    sx={getInputLabelStyles(Boolean(errors.allowedDomains))}
                  >
                    Domain
                  </InputLabel>
                  <Select
                    labelId="domain-select-label"
                    label="Domain"
                    multiple
                    value={userInfo.allowed_domains}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        allowedDomains: null,
                      }))
                    }
                    onChange={(e) => {
                      const value =
                        typeof e.target.value === "string"
                          ? [e.target.value]
                          : e.target.value;

                      if (value.includes("clear-selection")) {
                        setUserInfo((prev) => ({
                          ...prev,
                          allowed_domains: [],
                          allowed_Focus_Area: [],
                        }));
                        return;
                      }

                      setUserInfo((prev) => ({
                        ...prev,
                        allowed_domains: value,
                      }));
                    }}
                    slotProps={{
                      input: {
                        id: "domain",
                      },
                    }}
                    sx={getSelectStyles(Boolean(errors.allowedDomains))}
                  >
                    {domainTree != null && (
                      <MenuItem
                        key="clear-selection"
                        value="clear-selection"
                        disabled={userInfo.allowed_domains.length === 0}
                      >
                        Deselect all domains
                      </MenuItem>
                    )}
                    {domainTree != null &&
                      Object.keys(domainTree).map((domain: string) => (
                        <MenuItem key={domain} value={domain}>
                          {domain}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {errors.allowedDomains && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.allowedDomains}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <FormControl>
                  <InputLabel
                    id="focus-select-label"
                    htmlFor="focus"
                    sx={getInputLabelStyles(Boolean(errors.allowedFocusArea))}
                  >
                    Focus
                  </InputLabel>
                  <Select
                    labelId="focus-select-label"
                    label="Focus"
                    multiple
                    value={userInfo.allowed_Focus_Area}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        allowedFocusArea: null,
                      }))
                    }
                    onChange={(e) => {
                      const value =
                        typeof e.target.value === "string"
                          ? [e.target.value]
                          : e.target.value;

                      if (value.includes("clear-selection")) {
                        setUserInfo((prev) => ({
                          ...prev,
                          allowed_Focus_Area: [],
                        }));
                        return;
                      }

                      setUserInfo((prev) => ({
                        ...prev,
                        allowed_Focus_Area: value,
                      }));
                    }}
                    slotProps={{
                      input: {
                        id: "focus",
                      },
                    }}
                    sx={getSelectStyles(Boolean(errors.allowedFocusArea))}
                    renderValue={(selected) => {
                      const values = selected as string[];
                      if (!domainTree || values.length === 0) {
                        return values.join(", ");
                      }

                      return values
                        .map((focus) => {
                          for (const domain of userInfo.allowed_domains) {
                            const match = (domainTree[domain] || []).find(
                              (item) => item.focus === focus
                            );
                            if (match) {
                              return match.treatment_name || match.focus;
                            }
                          }
                          return focus;
                        })
                        .join(", ");
                    }}
                  >
                    {userInfo.allowed_domains.length === 0 && (
                      <MenuItem
                        key="select-domain"
                        value="select-domain"
                        disabled
                      >
                        Select at least one domain
                      </MenuItem>
                    )}
                    {userInfo.allowed_domains.length > 0 && (
                      <MenuItem
                        key="clear-selection"
                        value="clear-selection"
                        disabled={userInfo.allowed_Focus_Area.length === 0}
                      >
                        Deselect all focuses
                      </MenuItem>
                    )}
                    {userInfo.allowed_domains
                      .flatMap((domain) => {
                        if (!domainTree) return [];

                        return (domainTree[domain] || []).map((item) => ({
                          domain,
                          focus: item.focus,
                          treatment_name: item.treatment_name || item.focus,
                        }));
                      })
                      .map((pair) => (
                        <MenuItem key={pair.focus} value={pair.focus}>
                          {pair.domain} - {pair.treatment_name}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {errors.allowedFocusArea && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.allowedFocusArea}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="power-bi-email"
                  label="Power Bi Email"
                  type="email"
                  value={userInfo.power_bi_email ?? ""}
                  onChange={(e) =>
                    setUserInfo((prev) => ({
                      ...prev,
                      power_bi_email: e.target.value,
                    }))
                  }
                  onFocus={() =>
                    setErrors((prev) => ({
                      ...prev,
                      form: null,
                      powerBiEmail: null,
                    }))
                  }
                  sx={getTextFieldStyles(Boolean(errors.powerBiEmail))}
                />

                {errors.powerBiEmail && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.powerBiEmail}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <FormControl>
                  <InputLabel
                    id="is-demo-select-label"
                    htmlFor="is-demo"
                    sx={getInputLabelStyles(Boolean(errors.isDemo))}
                  >
                    Is demo
                  </InputLabel>
                  <Select
                    labelId="is-demo-select-label"
                    label="Is demo"
                    value={
                      userInfo.is_demo === undefined
                        ? ""
                        : userInfo.is_demo
                          ? "true"
                          : "false"
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        isDemo: null,
                      }))
                    }
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        is_demo: e.target.value === "true",
                      }))
                    }
                    slotProps={{
                      input: {
                        id: "is-demo",
                      },
                    }}
                    sx={getSelectStyles(Boolean(errors.isDemo))}
                  >
                    <MenuItem value="true" sx={menuItemStyles}>
                      Yes
                    </MenuItem>
                    <MenuItem value="false" sx={menuItemStyles}>
                      No
                    </MenuItem>
                  </Select>
                </FormControl>

                {errors.isDemo && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.isDemo}
                  </Typography>
                )}
              </Box>

              <Box sx={fieldContainerStyles}>
                <FormControl>
                  <InputLabel
                    id="not-display-in-statistics-select-label"
                    htmlFor="not-display-in-statistics"
                    sx={getInputLabelStyles(
                      Boolean(errors.notDisplayInStatistics)
                    )}
                  >
                    Not display in statistics
                  </InputLabel>
                  <Select
                    labelId="not-display-in-statistics-select-label"
                    label="Not display in statistics"
                    value={
                      userInfo.not_display_in_statistics === undefined
                        ? ""
                        : userInfo.not_display_in_statistics
                          ? "true"
                          : "false"
                    }
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                        },
                      },
                    }}
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        notDisplayInStatistics: null,
                      }))
                    }
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        not_display_in_statistics: e.target.value === "true",
                      }))
                    }
                    slotProps={{
                      input: {
                        id: "not-display-in-statistics",
                      },
                    }}
                    sx={getSelectStyles(
                      Boolean(errors.notDisplayInStatistics)
                    )}
                  >
                    <MenuItem value="true" sx={menuItemStyles}>
                      Yes
                    </MenuItem>
                    <MenuItem value="false" sx={menuItemStyles}>
                      No
                    </MenuItem>
                  </Select>
                </FormControl>

                {errors.notDisplayInStatistics && (
                  <Typography variant="body2" sx={errorTypographyStyles}>
                    {errors.notDisplayInStatistics}
                  </Typography>
                )}
              </Box>

              <Box sx={formBlockStyles}>
                <CustomTypography
                  fontSize={20}
                  fontWeight={600}
                  sx={{
                    color: theme.palette.modalColors.headerColor,
                  }}
                >
                  Professional Information
                </CustomTypography>

                <Box sx={fieldContainerStyles}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="job"
                    label="Job"
                    value={userInfo.professional_information.job ?? ""}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        professional_information: {
                          ...prev.professional_information,
                          job: e.target.value,
                        },
                      }))
                    }
                    onFocus={() =>
                      setErrors((prev) => ({ ...prev, form: null, job: null }))
                    }
                    sx={getTextFieldStyles(Boolean(errors.job))}
                  />

                  {errors.job && (
                    <Typography variant="body2" sx={errorTypographyStyles}>
                      {errors.job}
                    </Typography>
                  )}
                </Box>

                <Box sx={fieldContainerStyles}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="company"
                    label="Company"
                    value={userInfo.professional_information.company ?? ""}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        professional_information: {
                          ...prev.professional_information,
                          company: e.target.value,
                        },
                      }))
                    }
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        company: null,
                      }))
                    }
                    sx={getTextFieldStyles(Boolean(errors.company))}
                  />

                  {errors.company && (
                    <Typography variant="body2" sx={errorTypographyStyles}>
                      {errors.company}
                    </Typography>
                  )}
                </Box>

                <Box sx={fieldContainerStyles}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="department"
                    label="Department"
                    value={userInfo.professional_information.department ?? ""}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        professional_information: {
                          ...prev.professional_information,
                          department: e.target.value,
                        },
                      }))
                    }
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                        department: null,
                      }))
                    }
                    sx={getTextFieldStyles(Boolean(errors.department))}
                  />

                  {errors.department && (
                    <Typography variant="body2" sx={errorTypographyStyles}>
                      {errors.department}
                    </Typography>
                  )}
                </Box>

                <Box sx={fieldContainerStyles}>
                  <FormControl>
                    <InputLabel
                      id="role-select-label"
                      htmlFor="role"
                      sx={getInputLabelStyles(false)}
                    >
                      Role
                    </InputLabel>
                    <Select
                      labelId="role-select-label"
                      label="Role"
                      value={userInfo.professional_information.role}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      onFocus={() =>
                        setErrors((prev) => ({ ...prev, form: null }))
                      }
                      onChange={(e) =>
                        setUserInfo((prev) => ({
                          ...prev,
                          professional_information: {
                            ...prev.professional_information,
                            role: e.target.value,
                          },
                        }))
                      }
                      slotProps={{
                        input: {
                          id: "role",
                        },
                      }}
                      sx={getSelectStyles(false)}
                    >
                      <MenuItem key="user" value="user" sx={menuItemStyles}>
                        User
                      </MenuItem>
                      <MenuItem
                        key="line_manager"
                        value="line_manager"
                        sx={menuItemStyles}
                      >
                        Line Manager
                      </MenuItem>
                      <MenuItem
                        key="city_manager"
                        value="city_manager"
                        sx={menuItemStyles}
                      >
                        City Manager
                      </MenuItem>
                      <MenuItem
                        key="country_manager"
                        value="country_manager"
                        sx={menuItemStyles}
                      >
                        Country Manager
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={fieldContainerStyles}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="professional-bg"
                    label="Professional Background"
                    value={
                      userInfo.professional_information.professional_bg ?? ""
                    }
                    multiline
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: {
                        alignItems: "flex-start",
                        "& .MuiInputBase-inputMultiline": {
                          
                        },
                      },
                    }}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        professional_information: {
                          ...prev.professional_information,
                          professional_bg: e.target.value,
                        },
                      }))
                    }
                    onFocus={() =>
                      setErrors((prev) => ({ ...prev, form: null }))
                    }
                    sx={getTextFieldStyles(false)}
                  />
                </Box>

                <Box sx={fieldContainerStyles}>
                  <FormControl>
                    <InputLabel
                      id="country-select-label"
                      htmlFor="country"
                      sx={getInputLabelStyles(Boolean(errors.companyStructure))}
                    >
                      Countries
                    </InputLabel>
                    <Select
                      labelId="country-select-label"
                      label="Countries"
                      multiple
                      value={userInfo.professional_information.country}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      onFocus={() =>
                        setErrors((prev) => ({
                          ...prev,
                          form: null,
                          companyStructure: null,
                          country: null,
                        }))
                      }
                      onChange={(e) => {
                        const value =
                          typeof e.target.value === "string"
                            ? [e.target.value]
                            : e.target.value;

                        if (value.includes("clear-selection")) {
                          setUserInfo((prev) => ({
                            ...prev,
                            professional_information: {
                              ...prev.professional_information,
                              country: [],
                              city: [],
                              line: [],
                            },
                          }));
                          return;
                        }

                        setUserInfo((prev) => ({
                          ...prev,
                          professional_information: {
                            ...prev.professional_information,
                            country: value,
                          },
                        }));
                      }}
                      slotProps={{
                        input: {
                          id: "country",
                        },
                      }}
                      sx={getSelectStyles(Boolean(errors.companyStructure))}
                    >
                      {companyStructure != null && (
                        <MenuItem
                          key="clear-selection"
                          value="clear-selection"
                          disabled={
                            userInfo.professional_information.country.length ===
                            0
                          }
                        >
                          Deselect all countries
                        </MenuItem>
                      )}
                      {companyStructure != null &&
                        Object.keys(companyStructure).map((country: string) => (
                          <MenuItem key={country} value={country}>
                            {country}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={fieldContainerStyles}>
                  <FormControl>
                    <InputLabel
                      id="city-select-label"
                      htmlFor="city"
                      sx={getInputLabelStyles(Boolean(errors.companyStructure))}
                    >
                      Cities
                    </InputLabel>
                    <Select
                      labelId="city-select-label"
                      label="Cities"
                      multiple
                      value={userInfo.professional_information.city}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      onFocus={() =>
                        setErrors((prev) => ({
                          ...prev,
                          form: null,
                          city: null,
                          companyStructure: null,
                        }))
                      }
                      onChange={(e) => {
                        const value =
                          typeof e.target.value === "string"
                            ? [e.target.value]
                            : e.target.value;

                        if (value.includes("clear-selection")) {
                          setUserInfo((prev) => ({
                            ...prev,
                            professional_information: {
                              ...prev.professional_information,
                              city: [],
                              line: [],
                            },
                          }));
                          return;
                        }

                        setUserInfo((prev) => ({
                          ...prev,
                          professional_information: {
                            ...prev.professional_information,
                            city: value,
                          },
                        }));
                      }}
                      slotProps={{
                        input: {
                          id: "city",
                        },
                      }}
                      sx={getSelectStyles(Boolean(errors.companyStructure))}
                    >
                      {userInfo.professional_information.country.length ===
                        0 && (
                        <MenuItem
                          key="select-country"
                          value="select-country"
                          disabled
                        >
                          Select at least one country
                        </MenuItem>
                      )}
                      {userInfo.professional_information.country.length > 0 && (
                        <MenuItem
                          key="clear-selection"
                          value="clear-selection"
                          disabled={
                            userInfo.professional_information.city.length === 0
                          }
                        >
                          Deselect all cities
                        </MenuItem>
                      )}
                      {userInfo.professional_information.country
                        .flatMap((country) => {
                          if (!companyStructure) return [];

                          return Object.keys(companyStructure[country]);
                        })
                        .map((city) => (
                          <MenuItem key={city} value={city}>
                            {city}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={fieldContainerStyles}>
                  <FormControl>
                    <InputLabel
                      id="line-select-label"
                      htmlFor="line"
                      sx={getInputLabelStyles(Boolean(errors.companyStructure))}
                    >
                      Lines
                    </InputLabel>
                    <Select
                      labelId="line-select-label"
                      label="Lines"
                      multiple
                      value={userInfo.professional_information.line}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            maxHeight: 300,
                          },
                        },
                      }}
                      onFocus={() =>
                        setErrors((prev) => ({
                          ...prev,
                          form: null,
                          line: null,
                          companyStructure: null,
                        }))
                      }
                      onChange={(e) => {
                        const value =
                          typeof e.target.value === "string"
                            ? [e.target.value]
                            : e.target.value;

                        if (value.includes("clear-selection")) {
                          setUserInfo((prev) => ({
                            ...prev,
                            professional_information: {
                              ...prev.professional_information,
                              line: [],
                            },
                          }));
                          return;
                        }

                        setUserInfo((prev) => ({
                          ...prev,
                          professional_information: {
                            ...prev.professional_information,
                            line: value,
                          },
                        }));
                      }}
                      slotProps={{
                        input: {
                          id: "line",
                        },
                      }}
                      sx={getSelectStyles(Boolean(errors.companyStructure))}
                    >
                      {(userInfo.professional_information.country.length ===
                        0 ||
                        userInfo.professional_information.city.length ===
                          0) && (
                        <MenuItem
                          key="select-city"
                          value="select-city"
                          disabled
                        >
                          Select at least one city
                        </MenuItem>
                      )}
                      {userInfo.professional_information.country.length > 0 &&
                        userInfo.professional_information.city.length > 0 && (
                          <MenuItem
                            key="clear-selection"
                            value="clear-selection"
                            disabled={
                              userInfo.professional_information.line.length ===
                              0
                            }
                          >
                            Deselect all lines
                          </MenuItem>
                        )}
                      {userInfo.professional_information.country
                        .flatMap((country) => {
                          if (!companyStructure) return [];

                          const selectedCities = Object.keys(
                            companyStructure[country]
                          ).filter((countryCity) =>
                            userInfo.professional_information.city.includes(
                              countryCity
                            )
                          );

                          return selectedCities.flatMap(
                            (selectedCity) =>
                              companyStructure[country][selectedCity]
                          );
                        })
                        .flat()
                        .map((line) => (
                          <MenuItem key={line} value={line}>
                            {line}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  {errors.companyStructure && (
                    <Typography variant="body2" sx={errorTypographyStyles}>
                      {errors.companyStructure}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={formBlockStyles}>
                <CustomTypography
                  fontSize={20}
                  fontWeight={600}
                  sx={{
                    color: theme.palette.modalColors.headerColor,
                  }}
                >
                  Personal Information
                </CustomTypography>

                <Box sx={fieldContainerStyles}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    id="personal-country"
                    label="Country"
                    value={userInfo.personal_information.country ?? ""}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        personal_information: {
                          ...prev.personal_information,
                          country: e.target.value,
                        },
                      }))
                    }
                    onFocus={() =>
                      setErrors((prev) => ({
                        ...prev,
                        form: null,
                      }))
                    }
                    sx={getTextFieldStyles(false)}
                  />
                </Box>

                <Box sx={fieldContainerStyles}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="Choose birth date"
                      value={
                        birthDateTimestamp
                          ? dayjs(new Date(birthDateTimestamp))
                          : null
                      }
                      onChange={(newValue) => {
                        setErrors((prev) => ({
                          ...prev,
                          form: null,
                          dateOfBirth: null,
                        }));

                        if (!newValue) {
                          setBirthDateTimestamp(null);
                          return;
                        }

                        setBirthDateTimestamp(newValue.valueOf());
                      }}
                      minDate={dayjs(new Date(1900, 0))}
                      maxDate={dayjs(new Date())}
                      format="DD.MM.YYYY"
                      disableFuture
                      open={isBirthDatePickerOpen}
                      onOpen={() => {
                        setErrors((prev) => ({
                          ...prev,
                          form: null,
                          dateOfBirth: null,
                        }));
                        setIsBirthDatePickerOpen(true);
                      }}
                      onClose={() => setIsBirthDatePickerOpen(false)}
                      slotProps={{
                        field: {
                          clearable: true,
                        },
                        textField: {
                          fullWidth: true,
                          onClick: () => setIsBirthDatePickerOpen(true),
                          sx: getTextFieldStyles(Boolean(errors.dateOfBirth)),
                        },
                        popper: {
                          sx: popperStyles,
                        },
                        layout: {
                          sx: layoutStyles,
                        },
                        day: {
                          sx: dayStyles,
                        },
                        yearButton: {
                          sx: yearButtonStyles,
                        },
                      }}
                    />
                  </LocalizationProvider>

                  {errors.dateOfBirth && (
                    <Typography variant="body2" sx={errorTypographyStyles}>
                      {errors.dateOfBirth}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={buttonsContainerStyles}>
                <Button
                  variant="outlined"
                  onClick={handleClose}
                  sx={secondaryButtonStyles}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={mainButtonStyles}
                >
                  Create a user
                </Button>
              </Box>
            </form>
          )}
        </Box>
      </Modal>

      <CsvUploadModal
        open={isCsvUploadOpen}
        onClose={handleCloseCsvUpload}
        onSuccess={handleSuccessfulCsvUpload}
        adminCreds={adminCreds}
      />
    </>
  );
};
